/**
 * LandingScreen.jsx — Marketing landing page (Step 5).
 *
 * Rendered by AppGate for unauthenticated visitors who arrive at
 * anything other than an explicit sign-in flow (`#login`, `#login?
 * invite=…`, `#reset?token=…`, `#report/…`).  Provides:
 *
 *   • Navy hero with headline + subheadline + primary CTA ("Subscribe
 *     Now") that navigates to the enrollment form at `#enroll`
 *     (built in Step 6).
 *   • Sign-in link top-right → `#login`.
 *   • Three-up value-props strip (Board-ready · 30-year projections
 *     · Cancel anytime).
 *   • Feature grid (four cards: Scenarios · Risk-band Funding · PDF
 *     Reports · Multi-Association).
 *   • Simple pricing card — $50/mo, cancel anytime.  Second CTA at
 *     the bottom of the pricing card.
 *   • Slim navy footer with support + sign-in links.
 *
 * Palette is exactly the app's Navy / Sky / Coral tokens — nothing
 * new introduced here so the marketing page reads as a natural
 * extension of the product.
 */
import React from "react";
import WhyReserveModal from "../components/WhyReserveModal";
import TestimonialsModal from "../components/TestimonialsModal";


// 2026-02-28 (Dave) — Reusable palette values pulled from the
// project's CSS custom properties so a token retune (index.css line
// 9-21) automatically ripples through this page.
const NAVY = "#1C4E80";
const SKY = "#0091D5";
const CORAL = "#EA6A47";
const SLATE = "#5A6B75";
const SKY_WASH = "#F2F9FD";
const CORAL_WASH = "#FDF5F2";

// 2026-10-01 (Dave) — Official wordmark + tagline.  Shared by the
// topbar brandmark here and the EnrollScreen's slim brand bar.
const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/ehpgga4j_1.png";


function CtaButton({ label, variant = "primary", noArrow = false, "data-testid": testId, style: styleOverride, ...rest }) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "8px 28px",
    fontSize: 15,
    // 2026-10-02 (Dave) — bumped from 700 → 800 so the word reads
    // as unambiguously bold at the topbar's smaller 12.5px size.
    fontWeight: 800,
    letterSpacing: 0.4,
    borderRadius: 999,
    cursor: "pointer",
    border: "1.5px solid transparent",
    transition: "transform 120ms ease, background 120ms ease, color 120ms ease",
  };
  const variants = {
    primary: {
      background: CORAL,
      color: "#fff",
      boxShadow: "0 8px 24px rgba(234,106,71,0.35)",
    },
    ghost: {
      background: "transparent",
      color: NAVY,
      borderColor: NAVY,
    },
  };
  return (
    <button
      data-testid={testId}
      // Explicit merge: base < variant < caller-supplied overrides.
      // Without this, a caller passing `style={}` via rest would wipe
      // out background / color / fontWeight set above.
      style={{ ...base, ...variants[variant], ...(styleOverride || {}) }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
      {...rest}
    >
      {label}
      {!noArrow && <span style={{ fontSize: 18, lineHeight: 0.9 }}>→</span>}
    </button>
  );
}


function ValueProp({ eyebrow, title, body }) {
  return (
    <div style={{ padding: "8px 6px" }}>
      <div style={{
        fontSize: 13, letterSpacing: 0.3, textTransform: "uppercase",
        fontWeight: 700, color: CORAL, marginBottom: 8,
      }}>{eyebrow}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: NAVY, marginBottom: 8, lineHeight: 1.25 }}>
        {title}
      </div>
      <div style={{ fontSize: 16, lineHeight: 1.55, color: SLATE }}>
        {body}
      </div>
    </div>
  );
}


function FeatureCard({ title, body }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E2E8EC",
        borderLeft: `3px solid ${SKY}`,
        borderRadius: 4,
        padding: "18px 20px",
        transition: "border-left-color 160ms ease, box-shadow 160ms ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderLeftColor = CORAL;
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(28,78,128,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderLeftColor = SKY;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 700, color: NAVY, marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.55, color: SLATE }}>
        {body}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Hero carousel — 5 rotating slides.
// ---------------------------------------------------------------------------
// Slot 0 is the pure-CSS Comparative-Illustration mockup (kept so the
// hero always has a chart-flavored panel, no image asset required).
// Slots 1–4 are placeholder cards that get replaced with real image
// URLs once Dave delivers the marketing photography.
//
// TO SWAP IN A REAL IMAGE:
//   • Upload the file (Emergent Object Storage or `/public/hero/…`).
//   • Replace the matching `{ kind: "placeholder", … }` entry below
//     with `{ kind: "image", src: "<absolute or /hero/… path>",
//     alt: "<a11y label>" }`.
// Autoplay = 4.5s per slide with a 500ms cross-dissolve; pauses on
// hover.  Dots are click-navigable and reset the autoplay timer.
// ---------------------------------------------------------------------------
const HERO_SLIDES = [
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/5f4767d1d2681679_Screenshot%202026-10-07%20at%207.30.06%E2%80%AFAM.webp",
    alt: "Get Started in 3 Simple Steps — Step 1 List Your Common Elements, Step 2 Set 4 Key Assumptions, Step 3 Evaluate Scenarios & Decide",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/be200e1f41de8e4e_Screenshot%202026-10-07%20at%206.47.26%E2%80%AFAM.png",
    alt: "Common Elements · Shared Community Assets table listing Docks, Parking Lots (North & South), Restaining Villas, Retaining Wall, Timber Frame Steps, Villa Staining Tune Up, and Roofs — total extended cost $281,291",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/vgu5efrx_4.png",
    alt: "Scenario Variables — sliders for Reserve Fund Target, Inflation Rate, Interest Earned, and Year 1 Adjustment",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/dmdv69l7_5.png",
    alt: "Common Elements — Assets You Share table listing roofs, parking lots, villa staining, docks, retaining wall, timber steps and landscaping",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/c6e1668f128d691e_Screenshot%202026-10-07%20at%206.18.38%E2%80%AFAM.png",
    alt: "Reserve Spending Mix — donut chart showing $799,943 total over 30 years, led by Parking Lot South End (28.6%) and Parking Lot North End (27.6%), with Restaining Villas, Docks, Villa Staining Tune Up, Retaining Wall, and Timber Frame Steps",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/laks8us8_7.png",
    alt: "Funding Level widget — 15-year horizon showing 63% funding level with Target, Projected Balance, Shortfall and risk-band gauge",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/l0bw3ie3_8.png",
    alt: "Reserve Summary bar chart — 10-year horizon with Beginning, Contributions, Interest, Expenses, Special Assessment and Ending balances",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/wz9xy4zn_9.png",
    alt: "Funding Mix donut — 30-year horizon showing 93% Reserves, 7% Special Assessments on a $993K total, with per-member figures",
  },
  {
    kind: "image",
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_finance-audit-tool-4/artifacts/6qayo9h4_10.webp",
    alt: "Full Adams Court Association dashboard — Elements, Results at a Glance, Reserve Summary, Funding Mix, Scenario Variables, Funding Level, and Reserve Activity By Year",
  },
  { kind: "chart" },  // Comparative Illustration — appears last
];
const HERO_AUTOPLAY_MS = 4500;


function ChartSlide() {
  return (
    <div style={{ padding: "36px 32px 28px 32px", position: "relative" }}>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        gap: 12, marginBottom: 8,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
          Comparative Illustration
        </div>
        <div style={{
          fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase",
          color: "#B5D5EE", fontWeight: 700,
        }}>30-Year Horizon</div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 22, color: "#fff" }}>
        3 Calculation Methods Side-By-Side
      </div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 40, height: 160, marginBottom: 20 }}>
        {[
          { label: "Target",    heights: [46, 88, 132], color: SKY },
          { label: "Threshold", heights: [46, 62,  98], color: SKY },
          { label: "Cash Flow", heights: [52, 86,  70], color: SKY },
        ].map((clump) => (
          <div key={clump.label} style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 140 }}>
              <div style={{ flex: 1, height: clump.heights[0], background: "#B0B7BE", borderRadius: "3px 3px 0 0" }} />
              <div style={{ flex: 1, height: clump.heights[1], background: clump.color, borderRadius: "3px 3px 0 0" }} />
              <div style={{ flex: 1, height: clump.heights[2], background: "#7A828C", borderRadius: "3px 3px 0 0" }} />
            </div>
            <div style={{
              fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4,
              color: "#B5D5EE", marginTop: 10, fontWeight: 700, textAlign: "center",
            }}>{clump.label}</div>
          </div>
        ))}
      </div>
      {/* Target Funding Level horizontal line — coral, 3px, aligned
          with the top of the middle (center) column in the middle
          (Threshold) clump, which has height 62. */}
      <div style={{
        position: "absolute", left: 32, right: 32,
        bottom: 142, height: 3, background: CORAL, opacity: 1,
      }} />
      <div style={{
        fontSize: 10.5, color: "#B5D5EE", textAlign: "right",
        marginTop: -8, fontWeight: 500,
      }}>
        <span style={{ color: CORAL }}>▬</span> Target Funding Level (40%)
      </div>
    </div>
  );
}


function PlaceholderSlide({ label, hint }) {
  return (
    <div style={{
      height: "100%", minHeight: 300,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      gap: 8, padding: 36, textAlign: "center",
    }}>
      <div style={{
        fontSize: 10.5, letterSpacing: 0.4, textTransform: "uppercase",
        color: "#B5D5EE", fontWeight: 700,
      }}>{label}</div>
      <div style={{
        fontSize: 15, fontWeight: 700, color: "#fff",
      }}>Image slot</div>
      <div style={{
        fontSize: 12, color: "#B5D5EE", fontStyle: "italic", marginTop: 4,
      }}>{hint}</div>
    </div>
  );
}


function ImageSlide({ src, alt }) {
  return (
    <div style={{
      height: "100%", minHeight: 340,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px 24px 40px 24px",   // extra bottom-padding keeps
      overflow: "hidden",                //  the image clear of the dots
    }}>
      <div style={{
        background: "#FFFFFF",
        borderRadius: 8,
        padding: 12,
        width: "100%", height: "100%",
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
      }}>
        <img
          src={src}
          alt={alt || ""}
          style={{
            maxWidth: "100%", maxHeight: "100%",
            width: "auto", height: "auto",
            objectFit: "contain", display: "block",
          }}
        />
      </div>
    </div>
  );
}


function HeroCarousel() {
  const [idx, setIdx] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const timerRef = React.useRef(null);

  const goto = React.useCallback((i) => {
    setIdx(((i % HERO_SLIDES.length) + HERO_SLIDES.length) % HERO_SLIDES.length);
  }, []);

  React.useEffect(() => {
    if (paused) return undefined;
    timerRef.current = window.setTimeout(() => {
      setIdx((cur) => (cur + 1) % HERO_SLIDES.length);
    }, HERO_AUTOPLAY_MS);
    return () => window.clearTimeout(timerRef.current);
  }, [idx, paused]);

  return (
    <div
      data-testid="landing-hero-visual"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        background: `linear-gradient(160deg, ${NAVY} 0%, #12385C 100%)`,
        borderRadius: 12,
        color: "#fff",
        boxShadow: "0 24px 60px rgba(28,78,128,0.28)",
        position: "relative",
        overflow: "hidden",
        minHeight: 340,
      }}
    >
      {/* Stack every slide on top of each other; only the active one
          has opacity 1.  Absolute positioning keeps the panel a fixed
          height regardless of slide content. */}
      <div style={{ position: "relative", width: "100%", minHeight: 340 }}>
        {HERO_SLIDES.map((slide, i) => (
          <div
            key={i}
            data-testid={`landing-hero-slide-${i}`}
            aria-hidden={i !== idx}
            style={{
              position: i === 0 ? "relative" : "absolute",
              top: 0, left: 0, right: 0, bottom: 0,
              opacity: i === idx ? 1 : 0,
              transition: "opacity 500ms ease-in-out",
              pointerEvents: i === idx ? "auto" : "none",
            }}
          >
            {slide.kind === "chart" && <ChartSlide />}
            {slide.kind === "image" && <ImageSlide src={slide.src} alt={slide.alt} />}
            {slide.kind === "placeholder" && (
              <PlaceholderSlide label={slide.label} hint={slide.hint} />
            )}
          </div>
        ))}
      </div>

      {/* Dot indicators */}
      <div
        data-testid="landing-hero-dots"
        style={{
          position: "absolute", left: 0, right: 0, bottom: 14,
          display: "flex", justifyContent: "center", gap: 8,
          zIndex: 2,
        }}
      >
        {HERO_SLIDES.map((_, i) => {
          const active = i === idx;
          return (
            <button
              key={i}
              type="button"
              data-testid={`landing-hero-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => goto(i)}
              style={{
                width: active ? 22 : 8,
                height: 8,
                borderRadius: 999,
                background: active ? "#FFFFFF" : "rgba(255,255,255,0.45)",
                border: "none",
                padding: 0,
                cursor: "pointer",
                transition: "width 200ms ease, background 200ms ease",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------


export default function LandingScreen() {
  const goEnroll = () => { window.location.hash = "#enroll"; };
  const goLogin = () => { window.location.hash = "#login"; };
  const [whyOpen, setWhyOpen] = React.useState(false);
  const [quotesOpen, setQuotesOpen] = React.useState(false);

  // 2026-10-01 (Dave) — Marketing pages need normal page scroll;
  // the authenticated shell locks <html>/<body>/#root to 100vh with
  // overflow:hidden.  Toggle the escape-hatch class in index.css
  // while this screen is mounted and remove it on unmount so the
  // app shell's locked behavior is restored for logged-in routes.
  React.useEffect(() => {
    document.documentElement.classList.add("scroll-page");
    return () => document.documentElement.classList.remove("scroll-page");
  }, []);

  // 2026-09-30 (Dave) — Pull the admin-set monthly fee from the
  // anonymous platform-settings endpoint so every price label on
  // the marketing page (hero blurb, value-prop copy, pricing card)
  // reads from a single source of truth.  Falls back to 50.
  const [monthlyFee, setMonthlyFee] = React.useState(50);
  React.useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${backend}/api/platform-settings/anonymous`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && Number.isFinite(Number(d.monthly_fee_usd))) {
          setMonthlyFee(Number(d.monthly_fee_usd));
        }
      })
      .catch(() => {});
  }, []);

  return (
    <div
      data-testid="landing-screen"
      style={{
        minHeight: "100vh",
        background: "#FFFFFF",
        color: NAVY,
        fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      {/* ------------------------------------------------------------ */}
      {/*                          Top bar                             */}
      {/* ------------------------------------------------------------ */}
      <header
        data-testid="landing-topbar"
        style={{
          position: "sticky", top: 0, zIndex: 20,
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #E2E8EC",
        }}
      >
        <div
          className="flex items-center justify-between px-4 sm:px-8 py-3"
          style={{ maxWidth: 1200, margin: "0 auto" }}
        >
          <div className="sm:-ml-[18px]" style={{ display: "flex", alignItems: "center" }}>
            <img
              src={LOGO_URL}
              alt="uReserve Planning — Reserve Fund Planning Reimagined"
              className="h-[40px] sm:h-[64px]"
              style={{ width: "auto", display: "block" }}
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {/* 2026-10-02 (Dave) — Coral returning-user pill is the
                only topbar action now; its right edge naturally
                aligns with the carousel's right edge because both
                sit inside the same 1200px content frame with the
                same sm:px-8 right-padding. */}
            <div
              data-testid="landing-returning-pill"
              className="sm:mr-[115px]"
              onClick={goLogin}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") goLogin(); }}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "8px 16px",
                background: CORAL, color: "#fff",
                borderRadius: 999,
                fontSize: 12.5, fontWeight: 700,
                letterSpacing: 0.3,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 6px 16px rgba(234,106,71,0.3)",
                transition: "transform 120ms ease, box-shadow 120ms ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 8px 20px rgba(234,106,71,0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(234,106,71,0.3)";
              }}
            >
              <span className="hidden sm:inline">Already a registered user?</span>
              <span style={{ textDecoration: "underline", textUnderlineOffset: 2 }}>Sign In</span>
            </div>
                <CtaButton
                  data-testid="landing-hero-cta"
                  label="Subscribe Now"
                  onClick={goEnroll}
                  style={{
                    background: SKY_WASH,
                    color: "#000",
                    borderColor: SKY,
                    boxShadow: "none",
                  padding: "7px 18px",
                  whiteSpace: "nowrap",
                  }}
                />
                <div className="hidden md:block" style={{ fontSize: 14, color: SLATE, whiteSpace: "nowrap" }}>
                  <span style={{ color: NAVY, fontWeight: 700 }}>${monthlyFee}/month</span>
                  {" · "}
                  Cancel anytime
                </div>
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------ */}
      {/*                            Hero                              */}
      {/* ------------------------------------------------------------ */}
      <section
        data-testid="landing-hero"
        className="grid grid-cols-1 md:grid-cols-[1.15fr_1fr] gap-8 md:gap-10 items-center px-5 sm:px-8 pt-5 pb-6 md:pb-8 lg:pb-10"
        style={{ maxWidth: 1200, margin: "0 auto" }}
      >
        <div>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            padding: "8px 16px 8px 12px",
            background: SKY_WASH, borderRadius: 999,
            fontSize: 17, letterSpacing: 0.4, textTransform: "uppercase",
            fontWeight: 700, color: NAVY,
            marginBottom: 20,
            marginLeft: -30,   // left-shift so the inside text lines up with the H1 below
            lineHeight: 1.25,
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%",
              background: CORAL, flexShrink: 0,
            }} />
            <span>
              Reserve Fund Planning<br />
              For Common Interest Communities
            </span>
          </div>
          <h1
            className="text-[28px] sm:text-[34px] lg:text-[40px]"
            style={{
              lineHeight: 1.1, letterSpacing: -1,
              fontWeight: 800, color: NAVY, margin: 0,
            }}
          >
            Reserve planning solutions&nbsp;-{" "}
            <span style={{ color: CORAL, fontStyle: "italic" }}>inform</span>
            {" "}and{" "}
            <span style={{ color: CORAL, fontStyle: "italic" }}>
              engage
            </span>
            {" "}your membership.
          </h1>
          <p style={{
            fontSize: 16, lineHeight: 1.5, color: SLATE,
            marginTop: 14, marginBottom: 22, maxWidth: 540,
          }}>
            Build a durable reserve plan and budget in an afternoon.
            Model funding scenarios side-by-side, answer every
            &quot;what if&quot; question without touching a spreadsheet,
            and generate a member-ready PDF report.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <CtaButton
              data-testid="landing-why-reserve-btn"
              label="Why Reserve?"
              variant="ghost"
              noArrow
              onClick={() => setWhyOpen(true)}
              style={{ borderColor: NAVY }}
            />
            <CtaButton
              data-testid="landing-testimonials-btn"
              label="What Early Adopters Are Saying"
              variant="ghost"
              onClick={() => setQuotesOpen(true)}
              style={{ borderColor: NAVY }}
            />
          </div>
          <WhyReserveModal open={whyOpen} onClose={() => setWhyOpen(false)} />
          <TestimonialsModal open={quotesOpen} onClose={() => setQuotesOpen(false)} />
        </div>

        {/* Hero visual — rotating carousel. */}
        <HeroCarousel />
      </section>

      {/* ------------------------------------------------------------ */}
      {/*                    Value-props strip                         */}
      {/* ------------------------------------------------------------ */}
      <section
        data-testid="landing-valueprops"
        style={{
          background: SKY_WASH,
          borderTop: "1px solid #E2E8EC",
          borderBottom: "1px solid #E2E8EC",
        }}
      >
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-24 px-5 sm:px-8 py-8 sm:py-10"
          style={{ maxWidth: 1200, margin: "0 auto" }}
        >
          <ValueProp
            eyebrow="Board & Member Ready"
            title="Reports your membership will understand"
            body="Your plan renders to a paginated, plain-language report with executive summary, funding curve, and a full year-by-year table.  No writing work required."
          />
          <ValueProp
            eyebrow="Select your planning horizon — from 5 to 30 years"
            title="See the whole picture"
            body="Each saved scenario gives you accurate long-range projections for your annual contributions, adjustments, interest, expenditures, and special assessments if any."
          />
        </div>
      </section>

      {/* ------------------------------------------------------------ */}
      {/*         Legal strip — support email + trademark line         */}
      {/* ------------------------------------------------------------ */}
      <section
        data-testid="landing-legal"
        className="px-5 sm:px-8 py-5 sm:py-6"
        style={{
          maxWidth: 1200, margin: "0 auto",
          display: "flex", flexWrap: "wrap",
          alignItems: "center", justifyContent: "space-between",
          gap: 12,
          fontSize: 13, color: "#000",
        }}
      >
        <div data-testid="landing-legal-support">
          Email{" "}
          <a
            href="mailto:support@ureserveplanning.com"
            style={{ color: "#000", textDecoration: "underline" }}
          >support@ureserveplanning.com</a>
          {" "}for more information.
        </div>
        <div data-testid="landing-legal-trademark">
          Reserve Fund Planning Reimagined is a Trademark of uReserve Planning, LLC.
          All Rights Reserved &mdash; 2026.
        </div>
      </section>
    </div>
  );
}
